import { api } from '@/api'
import { BaseApi } from '@/api/BaseApi.ts'
import type {
  InitiativeCreatePermission,
  InitiativeData,
  InitiativeFetchParams,
  InitiativeFormData,
  InitiativeFullData,
  InitiativeLastCollect,
  InitiativeSearchCollectsParams,
  InitiativeSearchCollectsResult,
  InitiativeSearchParams,
  InitiativeSearchResult,
  InitiativeSummary,
} from '@/api/Initiative/types.ts'
import type { FetchParams, Paginated, ParamType } from '@/api/types.ts'
import type { UserData } from '@/api/User/types.ts'

class Api extends BaseApi<
  InitiativeFullData,
  Paginated<InitiativeData>,
  InitiativeFormData,
  InitiativeFetchParams
> {
  readonly baseUrl = '/initiatives'

  async fetchAdminUsers(
    params: ParamType[],
    fetchParams: FetchParams
  ): Promise<Paginated<UserData>> {
    const url = this.buildUrl(
      `${this.baseUrl}/:initiativeId/admin-users`,
      params,
      fetchParams
    )
    const { data } = await api.get<Paginated<UserData>>(url, this.config)
    return data
  }

  async createPermission(
    params: ParamType[],
    payload: InitiativeCreatePermission
  ): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:initiativeId/admin-users', params)}`
    const { data } = await api.post<void>(url, payload, this.config)
    return data
  }

  async removeAdminUser(params: ParamType[] = []): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:initiativeId/admin-users/:userId', params)}`
    const { data } = await api.delete<void>(url, this.config)
    return data
  }

  async duplicateInitiative(
    params: ParamType[],
    payload: InitiativeFormData
  ): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:initiativeId/duplicate', params)}`
    const { data } = await api.post<void>(url, payload, this.config)
    return data
  }

  async search(
    params: ParamType[],
    searchParams: InitiativeSearchParams
  ): Promise<InitiativeSearchResult[]> {
    const url = this.buildUrl(
      `${this.baseUrl}/:initiativeId/search`,
      params,
      searchParams
    )
    const { data } = await api.get<InitiativeSearchResult[]>(url, this.config)
    return data
  }

  async summary(params: ParamType[]): Promise<InitiativeSummary> {
    const url = this.getBaseUrl(this.baseUrl + '/:initiativeId/summary', params)
    const { data } = await api.get<InitiativeSummary>(url, this.config)
    return data
  }

  async lastCollects(params: ParamType[]): Promise<InitiativeLastCollect[]> {
    const url = this.getBaseUrl(
      this.baseUrl + '/:initiativeId/last-collects',
      params
    )
    const { data } = await api.get<InitiativeLastCollect[]>(url, this.config)
    return data
  }

  async searchCollects(
    params: ParamType[],
    searchParams: InitiativeSearchCollectsParams
  ): Promise<InitiativeSearchCollectsResult> {
    const url = this.buildUrl(
      `${this.baseUrl}/:initiativeId/search-collects`,
      params,
      searchParams
    )
    const { data } = await api.get<InitiativeSearchCollectsResult>(
      url,
      this.config
    )
    return data
  }
}

const InitiativeApi = new Api()

export default InitiativeApi
